/**
 * PromoBanner: growth surface at the top of the lobby / in-game main view.
 *
 * Shows exactly one contextual banner at a time (invite → Discord), centered and
 * max-width constrained, occupying a top slice so content flows below it. Web
 * only. Precedence is the pure `derivePromoBanner`; server inputs + session
 * dismissal live in `usePromoBannerStore`.
 *
 * Closeable (top-right X): dismissal is session-scoped (sessionStorage), so the
 * banner returns in a new web session until its action is completed.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { PROMO_BANNER_COPY, type PromoBannerKind } from '@danda/shared';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { usePromoBannerStore } from '@/lib/stores/promoBannerStore';
import { derivePromoBanner } from '@/lib/promo-banner-state';
import { track } from '@/lib/analytics';

function PromoBannerInner() {
  const uid = useAuthStore((s) => s.user?.uid ?? null);
  const status = usePromoBannerStore((s) => s.status);
  const dismissed = usePromoBannerStore((s) => s.dismissed);
  const setUser = usePromoBannerStore((s) => s.setUser);
  const hydrate = usePromoBannerStore((s) => s.hydrate);
  const refresh = usePromoBannerStore((s) => s.refresh);
  const dismiss = usePromoBannerStore((s) => s.dismiss);
  const openModal = useAccountModalStore((s) => s.openModal);
  const modalOpen = useAccountModalStore((s) => s.open);

  // Bind the store to the current account, then hydrate. `setUser` resets
  // status + dismissals on a uid change so an in-tab account switch never
  // shows the previous user's banner state.
  useEffect(() => {
    setUser(uid);
    hydrate();
  }, [uid, setUser, hydrate]);

  // The banner's Discord secondary CTA (and the invite CTA) open the account
  // modal, where the user may claim the Discord reward. Re-fetch on modal close
  // so a just-claimed reward (or newly converted referral) advances the banner
  // instead of leaving stale server state.
  const prevModalOpen = useRef(modalOpen);
  useEffect(() => {
    if (prevModalOpen.current && !modalOpen) refresh();
    prevModalOpen.current = modalOpen;
  }, [modalOpen, refresh]);

  // The invite banner advances to Discord only once a friend redeems the code,
  // which happens in a DIFFERENT session/tab. Re-fetch when the tab regains
  // focus so an external conversion (or reward claim) advances the banner
  // without a full reload. `focus` + `visibilitychange` both fire on a single
  // tab return, so coalesce them within a short window to avoid a double fetch.
  const lastFocusRefreshAt = useRef(0);
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastFocusRefreshAt.current < 1000) return;
      lastFocusRefreshAt.current = now;
      refresh();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const banner = status ? derivePromoBanner(status, dismissed) : null;

  // Fire `banner_impression` once per variant per mount.
  const impressed = useRef<Set<PromoBannerKind>>(new Set());
  useEffect(() => {
    if (banner && !impressed.current.has(banner)) {
      impressed.current.add(banner);
      track('banner_impression', { banner });
    }
  }, [banner]);

  if (!banner) return null;

  return (
    <div className="mb-2 w-full shrink-0">
      <div className="relative flex items-center gap-3 rounded-lg border border-accent-gold/40 bg-muted/60 px-4 py-2.5 pr-9 backdrop-blur-sm">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground">
            <span aria-hidden="true" className="mr-1.5">
              {PROMO_BANNER_COPY[banner].emoji}
            </span>
            {PROMO_BANNER_COPY[banner].title}
          </p>
          <p className="text-base text-foreground/80">
            {PROMO_BANNER_COPY[banner].body}
            {banner === 'invite' && (
              <>
                {' '}
                <a
                  href={PROMO_BANNER_COPY.invite.learnMoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-accent-gold underline underline-offset-2 hover:text-accent-gold/80"
                >
                  {PROMO_BANNER_COPY.invite.learnMoreLabel}
                </a>
              </>
            )}
          </p>
        </div>

        {banner === 'invite' ? (
          <Button
            size="sm"
            className="shrink-0 bg-accent-gold font-semibold text-background hover:bg-accent-gold/90"
            onClick={() => {
              track('banner_cta_click', { banner });
              openModal('invite');
            }}
          >
            {PROMO_BANNER_COPY.invite.cta}
          </Button>
        ) : (
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <Button
              size="sm"
              className="bg-accent-gold font-semibold text-background hover:bg-accent-gold/90"
              onClick={() => {
                track('banner_cta_click', { banner });
                window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer');
              }}
            >
              {PROMO_BANNER_COPY.discord.primaryCta}
            </Button>
            <button
              type="button"
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => {
                track('banner_cta_click', { banner: 'discord' });
                // The Rewards block (Discord OAuth connect + guild-membership
                // grant) lives in the account modal's Profile tab.
                openModal('profile');
              }}
            >
              {PROMO_BANNER_COPY.discord.secondaryCta}
            </button>
          </div>
        )}

        <button
          type="button"
          aria-label="Dismiss"
          className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => dismiss(banner)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function PromoBanner() {
  return (
    <ErrorBoundary fallback={null}>
      <PromoBannerInner />
    </ErrorBoundary>
  );
}
